import { Flex, Select, Stack } from "@mantine/core";
import { type JSX, useState } from "react";

import type { StepProps } from "../models";

import { createCase } from "../../../../../../api/createCase";
import { useSalesContext } from "../../../../../../sales-context/SalesContext";
import { useAnalytics } from "../../../../../../shared-utils/analytics/withPolymorphicClickAnalytics";
import { useVerifiedCustomerStore } from "../../../../../../stores";
import { type Feature, type Product, productMappings, products } from "../../../../../../types";
import { Header } from "../Header";
import { NavButtons } from "../NavButtons";
import classes from "./ProductCollectionStep.module.css";

export function ProductCollectionStep({ onNext, onPrev }: StepProps): JSX.Element {
    const { sessionId, logger, apiClients } = useSalesContext();
    const { dispatcher } = useAnalytics();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const callId = sessionId;

    const [selectedProduct, setSelectedProduct] = useState<Product | null>(
        (customer?.metadata?.product as Product | null) ?? null,
    );
    const [selectedFeature, setSelectedFeature] = useState<Feature | null>(
        (customer?.metadata?.feature as Feature | null) ?? null,
    );
    const [isLoading, setIsLoading] = useState(false);

    const onNextClick = async () => {
        if (!selectedProduct || !selectedFeature) return;
        setIsLoading(true);
        try {
            const caseDetails = customer
                ? await createCase(apiClients.customer, logger, dispatcher, {
                      email: customer.email,
                      firstName: customer.firstName,
                      lastName: customer.lastName,
                      feature: selectedFeature,
                      product: selectedProduct,
                      callId,
                  })
                : undefined;

            useVerifiedCustomerStore.getState().setCustomer(sessionId, {
                ...customer,
                metadata: {
                    ...customer?.metadata,
                    product: selectedProduct,
                    feature: selectedFeature,
                    caseId: caseDetails?.caseId,
                    caseNumber: caseDetails?.caseNumber,
                },
            });
            onNext?.();
        } catch {
            logger.warn("Error creating case");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Flex mt="sm" direction="column">
            <Header text="Which product and feature is the customer calling about?" />
            <Stack mt="md" mb="1.5rem">
                <Select
                    data-testid="select-product"
                    classNames={{ input: classes.styledSelect }}
                    radius="sm"
                    value={selectedProduct}
                    placeholder="Product"
                    onChange={(val) => {
                        setSelectedFeature(null);
                        setSelectedProduct(val as Product);
                    }}
                    data={[...products]}
                />
                <Select
                    data-testid="select-feature"
                    classNames={{ input: classes.styledSelect }}
                    value={selectedFeature}
                    radius="sm"
                    placeholder="Feature"
                    onChange={(val) => setSelectedFeature(val as Feature | null)}
                    disabled={selectedProduct === null}
                    data={selectedProduct ? productMappings[selectedProduct] : []}
                />
            </Stack>
            <NavButtons
                isLoading={isLoading}
                onNext={onNext && selectedProduct && selectedFeature ? () => void onNextClick() : undefined}
                onPrev={onPrev}
            />
        </Flex>
    );
}
